import { useTranslation } from "react-i18next";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "api/axios";
import Search from "assets/icons/search.gif";
import {
  DataGridPremium,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridColDef,
  GridColumnVisibilityModel,
  GridInitialState,
  GridRenderCellParams,
  GridRowGroupingModel,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from "@mui/x-data-grid-premium";
import moment from "moment";
import PackagesContext from "contexts/PackagesContext";
import KeysContext from "contexts/KeysContext";
import { IPackage } from "dto/IPackage";
import tagsService from "api/tagsService";
import statusService from "api/statusService";
import { IAttachment } from "dto/IAttachment";
import MenuCell from "./Cells/MenuCell";
import FormatCell from "./Cells/FormatCell";
import { plGridLocales } from "utils/muiGridLocales";
import { nullHandlingSortComparator } from "utils/nullHandlingSortComparator";
import CustomToolbar from "components/CaseDossier/Toolbar";
import Spinner from "components/Spinner";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { ListType } from "dto/ListType";
import { StatusType } from "dto/IStatus";
import { TagType } from "dto/ITag";
import { IGridFilter } from "dto/IGridFilter";
import { ItemKind } from "dto/IKindItems";
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

const CaseCourtPortal: FC<Props> = ({
  handleAddToDossier,
  caseId,
  searchText,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isFullScreen = window.location.href.search("fullscreen") !== -1;

  const { data: tagsData } = tagsService.useGetTags();
  const { data: statusesData, isLoading: isLoadingStatuses } =
    statusService.useGetStatuses();

  const { packages, setPackages } = useContext(PackagesContext);
  const { keys, setKeys } = useContext(KeysContext);

  const [key, setKey] = useState(0);
  const gridSettingsStorageKey = () => "grid_caseCourtPortal_v4";

  const [filterStatusId, setFilterStatusId] = useState<number>(null);
  const [filterTagId, setFilterTagId] = useState<string>(null);
  const [filterUserId, setFilterUserId] = useState<string>(null);
  const [editFilter, setEditFilter] = useState<IGridFilter>(null);

  const CustomNoRowsOverlay = () => {
    return (
      <div className={styles.empty}>
        <img src={Search} />
        <div>{t("emptyStates.noResults")}</div>
      </div>
    );
  };

  const CustomToolbar2 = () => {
    return (
      <CustomToolbar
        listName="messagesRegister"
        editFilter={editFilter}
        setEditFilter={setEditFilter}
        listType={ListType.Incoming}
        statusId={filterStatusId}
        setStatusId={setFilterStatusId}
        tagId={filterTagId}
        setTagId={setFilterTagId}
        statusType={StatusType.INCOMING}
        tagType={TagType.Incoming}
        userId={filterUserId}
        setUserId={setFilterUserId}
        dateFilterLabel="Data"
      ></CustomToolbar>
    );
  };

  const toolbar = () => <CustomToolbar2></CustomToolbar2>;

  useEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem(
      gridSettingsStorageKey()
    );
    const data: GridInitialStatePremium = stateFromLocalStorage
      ? JSON.parse(stateFromLocalStorage)
      : ({} as GridInitialStatePremium);
    setInitialState(data);
    initialStateHidden.columns = data.columns;
    initialStateHidden.rowGrouping = data.rowGrouping;
    setGroupingModel(data.rowGrouping?.model ?? []);

    setKey(key + 1);
  }, []);

  const {
    data,
    fetchNextPage,
    isLoading,
    hasNextPage,
    refetch,
  } = useInfiniteQuery(
    ["packages", filterStatusId, filterTagId, filterUserId],
    async ({ pageParam = "" }) => {
      const query = new URLSearchParams(window.location.search);
      // if (!!searchText.length) {
      //   query.append("Text", searchText);
      // }

      query.append("CaseId", caseId);
      query.append("IsIncoming", "true");
      query.append("IncludeRemoved", "true");
      query.append("FromRegistry", "true");
      query.append("Group", "true");
      query.append("Kind", ItemKind.CourtPortalDocument.toString());
      query.append("PageToken", pageParam);
      query.append("SortProp", "Date");
      query.append("SortOrder", "Descending");

      if (filterStatusId) {
        query.append("StatusId", filterStatusId.toString());
      }

      if (filterTagId) {
        query.append("TagId", filterTagId.toString());
      }

      const res = await axiosInstance.get(`/list?${query}`);
      return res.data;
    },
    {
      enabled: false,
      staleTime: 0,
      cacheTime: 0,
      getNextPageParam: (lastPage) => {
        if (!lastPage.LastPage) {
          return lastPage.PageToken;
        }
        return undefined;
      },
    }
  );

  const [isInitialized, setIsInitialized] = useState(false);

  const compileData = async () => {
    if (!data) return;

    const packs = await Promise.all(
      data?.pages
        .map((page) => {
          return page.Items;
        })
        .flat()

      // .sort((left: IPackage, right: IPackage) => {
      //   //@ts-ignore
      //   return right.Unread - left.Unread;
      // })
    );

    const newMap = packs.map((p: IPackage) => {
      const oldPack = packages.find((a) => a.Id === p.Id);

      if (oldPack?.isDelete) {
        p.isDelete = true;
      }

      return p;
    });

    const objectKeys = await Promise.all(
      data?.pages
        .map((page) => {
          return page.ObjectKeys;
        })
        .flat()
    );

    setPackages(newMap.flat());
    // @ts-ignore
    setKeys((prev) => [...prev, ...objectKeys]);
    setIsInitialized(true);
  };

  const onDownload = async (
    pack: IPackage,
    attachment: IAttachment,
    key?: unknown
  ) => {
    // setDownloadFileName(attachment.FileName);
    // create(pack.Id, attachment, pack.Kind, true);

    return;
  };

  const onAddToDossier = (pack: IPackage) => {
    handleAddToDossier(pack);
  };

  useEffect(() => {
    compileData();
  }, [data]);

  useEffect(() => {
    setPackages([]);
    setIsInitialized(false);

    setTimeout(() => {
      refetch();
    }, 500);
  }, [searchText, filterStatusId, filterTagId, filterUserId, caseId]);

  const apiRef = useGridApiRef();

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const [groupingModel, setGroupingModel] = useState<GridRowGroupingModel>([]);

  const saveSnapshotInternal = () => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem(
        gridSettingsStorageKey(),
        JSON.stringify(currentState)
      );
    }
  };

  const saveSnapshot = useCallback(() => {
    saveSnapshotInternal();
  }, [apiRef]);

  const [initialState, setInitialState] = useState<GridInitialState>(null);

  const initialStateHidden = useKeepGroupedColumnsHidden({
    apiRef,
    initialState,
  });

  useLayoutEffect(() => {
    console.log("Layout effect");
    const stateFromLocalStorage = localStorage?.getItem(
      gridSettingsStorageKey()
    );
    const data: GridInitialStatePremium = stateFromLocalStorage
      ? JSON.parse(stateFromLocalStorage)
      : ({} as GridInitialStatePremium);
    setInitialState(data);
    initialStateHidden.columns = data.columns;
    initialStateHidden.rowGrouping = data.rowGrouping;
    setGroupingModel(data.rowGrouping?.model ?? []);

    // handle refresh and navigating away/refreshing
    window.addEventListener("beforeunload", saveSnapshot);

    return () => {
      // in case of an SPA remove the event-listener
      window.removeEventListener("beforeunload", saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  const hiddenFields = [
    "Label",
    "IsFolder",
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    "Icons",
    "Menu",
  ];

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);
  };

  const columns: GridColDef[] = [
    {
      field: "S1",
      minWidth: 60,
      flex: 1,
      headerName: t("courtPortal.documentTitle"),
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.S1,
      sortComparator: nullHandlingSortComparator,
      groupingValueGetter: (value, row) => row.Sender,
      renderCell: (params: GridRenderCellParams<IPackage, string>) =>
        params.rowNode.type !== "group" ? (
          <FormatCell element={params.row}></FormatCell>
        ) : (
          <div>{params.value}</div>
        ),
    },

    {
      field: "Type",
      width: 140,
      headerName: t("common.type"),
      groupable: false,
    },

    {
      field: "SendDate",
      width: 140,
      headerName: t("courtPortal.published"),
      groupable: false,
      valueFormatter: (v) => {
        if (v) {
          return moment(v).format("yy.MM.DD");
        }
        return null;
      },
    },

    // {
    //   field: "Icons",
    //   width: 140,
    //   headerName: "Ikony",
    //   disableReorder: true,
    //   sortable: false,
    //   resizable: false,
    //   hideable: false,
    //   groupable: false,
    //   disableColumnMenu: true,
    //   renderHeader: () => <div></div>,
    //   renderCell: (params: GridRenderCellParams<IPackage, string>) => (
    //     <IconsCell
    //       element={params.row}
    //       onDownload={onDownload}
    //       onPreview={(pack, attachment) => {
    //         const newUrl = `${window.location.pathname}?pdf=${
    //           pack.Id
    //         }:${attachment.FileName.replace(":", "")}:${
    //           attachment.Uri ?? attachment.Id
    //         }:${pack.Kind}:${pack.EncryptionKeyId}`;

    //         navigate(newUrl);
    //       }}
    //       keys={keys}
    //     ></IconsCell>
    //   ),
    // },
    {
      field: "Menu",
      width: 40,
      headerName: "Menu",
      disableReorder: true,
      sortable: false,
      resizable: false,
      hideable: false,
      groupable: false,
      disableColumnMenu: true,
      renderHeader: () => <div></div>,
      renderCell: (params: GridRenderCellParams<IPackage, string>) => (
        <MenuCell
          element={params.row}
          onDownload={onDownload}
          onPreview={(pack, attachment) => {
            const newUrl = `${window.location.pathname}?pdf=${
              pack.Id
            }:${attachment.FileName.replace(":", "")}:${
              attachment.Uri ?? attachment.Id
            }:${pack.Kind}:${pack.EncryptionKeyId}`;

            navigate(newUrl);
          }}
          keys={keys}
          onAddToDossier={onAddToDossier}
        ></MenuCell>
      ),
    },
  ];

  const columnsMemo = useMemo(() => columns, []);

  if (
    !initialState ||
    !statusesData?.data ||
    !tagsData?.data ||
    !initialStateHidden
  ) {
    return <Spinner />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.list}>
        {/* {packages.length === 0 && !isLoading && isInitialized && (
        
        )} */}

        <DataGridPremium
          key={key}
          apiRef={apiRef}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
          initialState={initialState}
          localeText={plGridLocales}
          slots={{ toolbar: toolbar, noRowsOverlay: CustomNoRowsOverlay }}
          showToolbar
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            columnsManagement: {
              getTogglableColumns,
            },
          }}
          loading={!isInitialized || isLoading || isLoadingStatuses}
          columnHeaderHeight={30}
          onRowsScrollEnd={() => {
            if (hasNextPage) {
              fetchNextPage();
            }
          }}
          onRowClick={() => {}}
          onRowDoubleClick={(r) => {
            const p = r.row as IPackage;

            const newUrl = `${window.location.pathname}?pdf=${
              p.Id
            }:${p.Attachments[0].FileName.replace(":", "")}:${
              p.Attachments[0].Uri ?? p.Attachments[0].Id
            }:${p.Kind}:${p.EncryptionKeyId}${
              isFullScreen ? "&fullscreen=1" : ""
            }`;

            navigate(newUrl);

            if (window.getSelection) {
              if (window.getSelection().empty) {
                // Chrome
                window.getSelection().empty();
              } else if (window.getSelection().removeAllRanges) {
                // Firefox
                window.getSelection().removeAllRanges();
              }
            } else if (document.selection) {
              // IE?
              document.selection.empty();
            }
          }}
          columns={columnsMemo}
          hideFooter={true}
          rows={packages.map((c) => {
            //@ts-ignore
            c.id = c.Id;
            return c;
          })}
          disableColumnFilter={true}
          disableAggregation={true}
          onColumnVisibilityModelChange={(m) => {
            console.log("ON Column visibility model changed", m);
            setColumnVisibilityModel(m);
            saveSnapshot();
          }}
          onRowGroupingModelChange={(m) => {
            const newVisibility = { ...columnVisibilityModel };

            for (let i = 0; i < groupingModel.length; i++) {
              newVisibility[groupingModel[i]] = true;
            }

            for (let i = 0; i < m.length; i++) {
              newVisibility[m[i]] = false;
            }
            setColumnVisibilityModel(newVisibility);
            setGroupingModel(m);
          }}
          columnVisibilityModel={columnVisibilityModel}
        />
      </div>
    </div>
  );
};

export default CaseCourtPortal;
